import RateDisplay from "@components/Cards/RateDisplay";
import { useFormatContext } from "@lib/hooks/format-context";
import { CellContext, HeaderContext } from "@tanstack/react-table";
import React from "react";
import { getTotalValueOfAllRows, getCorrelation } from "./calculations";

type Props<T> = {
  compared?: boolean;
  highlightMinMaxValues?: boolean;
} & (
  | {
      info: CellContext<T, number | undefined>;
      calculateTotals?: false;
      total?: never;
      comparedTotal?: never;
    }
  | {
      info: HeaderContext<T, number | undefined>;
      calculateTotals: true;
      total?: never;
      comparedTotal?: never;
    }
  | {
      total: number;
      comparedTotal?: number;
      info?: never;
      calculateTotals?: never;
    }
);
export type CurrencyCalculatorType = (
  value: number | undefined,
  numDigits: number,
  displaySymbol?: boolean
) => string;

const getDisplayString = (
  value: number,
  currencyCalculator: CurrencyCalculatorType
) => {
  return currencyCalculator(value, 2);
};

function AovTableDisplay<T extends Record<string, any>>({
  info,
  compared,
  calculateTotals,
  highlightMinMaxValues,
  total,
  comparedTotal,
}: Props<T>) {
  const { calculateCurrencyConvertion } = useFormatContext();
  let comparedValue = 0;
  let value = 0;

  if (total !== undefined) {
    value = total;
    comparedValue = comparedTotal ?? 0;
  } else if (calculateTotals && info) {
    const allOrderValues = info.column.getFacetedRowModel().rows.map((row) => {
      return {
        value: row.original?.["purchaseCount"],
        compared: row.original?.compared?.["purchaseCount"],
      };
    });
    const allRevenueValues = info.column
      .getFacetedRowModel()
      .rows.map((row) => {
        return {
          value: row.original?.["purchaseAmount"],
          compared: row.original?.compared?.["purchaseAmount"],
        };
      });
    const orderTotals = getTotalValueOfAllRows({
      values: allOrderValues,
    });
    const revenueTotals = getTotalValueOfAllRows({
      values: allRevenueValues,
    });

    value =
      revenueTotals.value && orderTotals.value
        ? revenueTotals.value / orderTotals.value
        : 0;
    comparedValue =
      revenueTotals.compared && orderTotals.compared
        ? revenueTotals.compared / orderTotals.compared
        : 0;
  } else if (info) {
    value = info.getValue() ?? 0;
    if (compared && info.row.original?.compared) {
      comparedValue = info.row.original?.compared[info.column?.id];
    }
  }
  let valueClassNames = "";
  if (highlightMinMaxValues && info) {
    const values = info.column.getFacetedMinMaxValues();
    if (
      values &&
      ((Array.isArray(values[0]) && values[0][0] === value) ||
        values[0] === value)
    ) {
      valueClassNames =
        "bg-green-500/20 dark:bg-green-600/20 text-green-800 dark:text-green-200 py-0.5 px-1.5 rounded-lg";
    } else if (
      values &&
      ((Array.isArray(values[1]) && values[1][0] === value) ||
        values[1] === value)
    ) {
      valueClassNames =
        "bg-green-500 dark:bg-green-600 text-green-100 py-0.5 px-1.5 rounded-lg";
    }
  }
  if (compared) {
    const rate = getCorrelation(value, comparedValue, 2);
    return (
      <div className="flex items-center w-full">
        <div className="flex-1 flex items-start flex-col">
          <p
            className={value > 0 ? valueClassNames : ""}
            data-display="currency"
          >
            {getDisplayString(value, calculateCurrencyConvertion)}
          </p>
          <p
            className="text-[11px] leading-tight opacity-70"
            data-display="currency"
            data-compared={true}
          >
            vs {getDisplayString(comparedValue, calculateCurrencyConvertion)}
          </p>
        </div>
        <RateDisplay rate={rate} isIncreasing={rate > 0} size="small" />
      </div>
    );
  }
  return (
    <p data-display="currency">
      {getDisplayString(value, calculateCurrencyConvertion)}
    </p>
  );
}

export default AovTableDisplay;
